import {
  getAttendanceField,
  getPunchMillis,
  normalizePunchType,
} from "../explorer/attendanceFilterConfig";

/**
 * Attendance anomaly detection — duplicates, missing OUT and missing IN.
 * Operates on already-loaded/filtered explorer rows (SDK only, no extra reads),
 * reusing the flexible attendance field resolvers.
 */

function dayKey(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function fmtTime(ms) {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * Detect duplicate punches: same employee + same device + same minute.
 * @returns {{ groups: Array, duplicateIds: Set<string>, count: number }}
 */
export function detectDuplicates(rows = []) {
  const groups = new Map();
  rows.forEach((row) => {
    const emp = String(getAttendanceField(row, "employeeId") ?? "");
    const device = String(getAttendanceField(row, "device") ?? "");
    const ms = getPunchMillis(row);
    if (!emp || ms == null) return;
    const key = `${emp}|${device}|${Math.floor(ms / 60000)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ id: row.id, emp, device, ms, row });
  });

  const duplicateIds = new Set();
  const outGroups = [];
  groups.forEach((list, key) => {
    if (list.length > 1) {
      list.forEach((item) => duplicateIds.add(item.id));
      outGroups.push({
        key,
        employeeId: list[0].emp,
        device: list[0].device,
        time: fmtTime(list[0].ms),
        count: list.length,
        ids: list.map((i) => i.id),
      });
    }
  });

  return { groups: outGroups.sort((a, b) => b.count - a.count), duplicateIds, count: duplicateIds.size };
}

/** Build per employee/day ordered punch sequences. */
function buildSequences(rows) {
  const byKey = new Map();
  rows.forEach((row) => {
    const emp = String(getAttendanceField(row, "employeeId") ?? "");
    const ms = getPunchMillis(row);
    if (!emp || ms == null) return;
    const key = `${emp}|${dayKey(ms)}`;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push({
      id: row.id,
      emp,
      ms,
      type: normalizePunchType(getAttendanceField(row, "punchType")),
      row,
    });
  });
  byKey.forEach((list) => list.sort((a, b) => a.ms - b.ms));
  return byKey;
}

/**
 * Detect OUT punches with no preceding IN in the same employee/day sequence.
 * @returns {{ ids: Set<string>, records: Array }}
 */
export function detectMissingIn(rows = []) {
  const sequences = buildSequences(rows);
  const ids = new Set();
  const records = [];
  sequences.forEach((list, key) => {
    let seenIn = false;
    list.forEach((p) => {
      if (p.type === "IN") seenIn = true;
      else if (p.type === "OUT" && !seenIn) {
        ids.add(p.id);
        records.push({ key, employeeId: p.emp, time: fmtTime(p.ms), id: p.id, issue: "OUT without previous IN" });
      }
    });
  });
  return { ids, records };
}

/**
 * Detect IN punches with no later OUT in the same employee/day sequence.
 * @returns {{ ids: Set<string>, records: Array }}
 */
export function detectMissingOut(rows = []) {
  const sequences = buildSequences(rows);
  const ids = new Set();
  const records = [];
  sequences.forEach((list, key) => {
    // The last unmatched IN of the day has no following OUT.
    let openIn = null;
    list.forEach((p) => {
      if (p.type === "IN") openIn = p;
      else if (p.type === "OUT") openIn = null;
    });
    if (openIn) {
      ids.add(openIn.id);
      records.push({ key, employeeId: openIn.emp, time: fmtTime(openIn.ms), id: openIn.id, issue: "IN without later OUT" });
    }
  });
  return { ids, records };
}
